import get from 'lodash/get';

import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { createSubcampaign, removeSubcampaign } from 'modules/campaigns/reducers/campaignSubcampaigns';
import { fetchBrandCampaigns, fetchSubcampaignsByCampaignIds } from 'modules/campaigns/reducers/brandCampaigns';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { logError } from 'utils/log';
import { t } from 'utils/language';

import type { Campaign, CampaignShortlistsReduxState, CheckboxesListItem } from 'types';

const campaignsQuery = {
  state: ['active', 'preparation'],
  itemsPerPage: 1000,
  filter: 'brand'
};

const SUBCAMPAIGN_CREATED = 'shortlists/SUBCAMPAIGN_CREATED';
const SUBCAMPAIGN_REMOVED = 'shortlists/SUBCAMPAIGN_REMOVED';
const RECEIVE_CAMPAIGNS = 'shortlists/RECEIVE_CAMPAIGNS';
const LOADING_CAMPAIGNS = 'shortlists/LOADING_CAMPAIGNS';
const RESET_CAMPAIGNS = 'shortlists/RESET_CAMPAIGNS';

const initialState: CampaignShortlistsReduxState = {
  list: [],
  isLoading: false
};

function transformSubcampaign(subcampaign) {
  return {
    ...subcampaign,
    creatorId: get(subcampaign, 'creatorProfile.id')
  };
}

function subcampaignCreatedInShortlist(campaignId, subcampaign) {
  return {
    type: SUBCAMPAIGN_CREATED,
    payload: {
      campaignId,
      subcampaign
    }
  };
}

function subcampaignRemovedFromShortlist(campaignId, subcampaignId) {
  return {
    type: SUBCAMPAIGN_REMOVED,
    payload: {
      campaignId,
      subcampaignId
    }
  };
}

function receiveCampaigns(data) {
  return {
    type: RECEIVE_CAMPAIGNS,
    payload: {
      data
    }
  };
}

function loadingCampaigns() {
  return {
    type: LOADING_CAMPAIGNS,
    payload: {}
  };
}

export function resetShortlists() {
  return {
    type: RESET_CAMPAIGNS,
    payload: {}
  };
}

export function fetchShortlists(filter: string) {
  return async (dispatch, getState) => {
    const organizationId = getState().organizations.currentOrganizationId;

    if (!organizationId) {
      return Promise.resolve();
    }

    dispatch(loadingCampaigns());

    let campaignsList: Array<Campaign> = [];

    try {
      const campaigns = await dispatch(fetchBrandCampaigns(organizationId, { ...campaignsQuery, filter }));
      campaignsList = campaigns.data || [];

      const subcampaignsByCampaignIdsMap = await dispatch(fetchSubcampaignsByCampaignIds(
        campaignsList.map(c => c.id))
      );

      campaignsList = campaignsList.map(campaign => ({
        ...campaign,
        subcampaigns: subcampaignsByCampaignIdsMap[campaign.id] || []
      }));

      dispatch(receiveCampaigns(campaignsList));
    } catch (error) {
      dispatch(receiveCampaigns([]));

      throw error;
    }

    return campaignsList;
  };
}

export function addToShortlists(influencerId, item) {
  return async (dispatch) => {
    const url = 'addToShortlists()';

    dispatch(showLoading(url));

    try {
      const subcampaign = await dispatch(createSubcampaign(influencerId, item.campaignId));
      dispatch(subcampaignCreatedInShortlist(item.campaignId, transformSubcampaign(subcampaign)));
    } catch (error) {
      logError(error);
      dispatch(danger(t('unknown_error')));
    }

    dispatch(hideLoading(url));
  };
}

export function removeFromShortlists({ subcampaign, campaignId }: Partial<CheckboxesListItem>) {
  return async (dispatch) => {
    const url = 'removeFromShortlists()';

    dispatch(showLoading(url));

    try {
      await dispatch(removeSubcampaign(subcampaign, campaignId));
      dispatch(subcampaignRemovedFromShortlist(campaignId, subcampaign?.id));
    } catch (error) {
      logError(error);
      dispatch(danger(t('unknown_error')));
    }

    dispatch(hideLoading(url));
  };
}

export default createReducer<CampaignShortlistsReduxState, any>('shortlists', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET_CAMPAIGNS]: () => initialState,
  [SUBCAMPAIGN_CREATED]: (state, { campaignId, subcampaign }) => ({
    ...state,
    list: state.list.map(campaign => (campaign.id === campaignId
      ? ({
        ...campaign,
        subcampaigns: [...campaign.subcampaigns, subcampaign]
      })
      : campaign
    ))
  }),
  [SUBCAMPAIGN_REMOVED]: (state, { campaignId, subcampaignId }) => ({
    ...state,
    list: state.list.map(campaign => (campaign.id === campaignId
      ? ({
        ...campaign,
        subcampaigns: campaign.subcampaigns.filter(s => s.id !== subcampaignId)
      })
      : campaign
    ))
  }),
  [RECEIVE_CAMPAIGNS]: (state, { data }) => ({
    ...state,
    list: data,
    isLoading: false
  }),
  [LOADING_CAMPAIGNS]: (state) => ({
    ...state,
    list: [],
    isLoading: true
  })
});
